/**
 * BulkAttachDocsDialog · Feb 2026 (iter1086h)
 *
 * When a single payment covers several bank-feed rows (e.g. a "GTB
 * SWEEP" that clears three invoices), attach the SAME receipt to every
 * selected row in one gesture.
 *
 * Flow:
 *   1. User selects N bank rows → clicks "Attach Doc" in the bulk toolbar.
 *   2. This dialog opens with a drop-zone. On upload, the file is
 *      uploaded ONCE via `/api/documents/upload` linking to the first
 *      selected entity.
 *   3. The returned document_id is then linked to every remaining
 *      selected entity via `/api/documents/{doc_id}/links`.
 *   4. Progress is shown per row; failures are logged but do not abort
 *      the batch (partial links are still valuable).
 *
 * Reusable beyond bank feeds: any list with multi-select can pass its
 * entityType + entityIds and get the same UX.
 */
import React, { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

const ACCEPT_TYPES = 'image/png,image/jpeg,image/gif,image/webp,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain,.pdf,.png,.jpg,.jpeg,.gif,.webp,.xls,.xlsx,.csv,.txt';
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

const LEGACY_DOC_TYPE = {
  invoice: 'invoice_support',
  bill: 'bill_support',
  payment: 'payment_proof',
};

export const BulkAttachDocsDialog = ({
  open,
  onOpenChange,
  entityType = 'bank_feed_txn',
  entityIds = [],
  uploadedVia,
  onCompleted,
}) => {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | uploading | linking | done
  const [linkedCount, setLinkedCount] = useState(0);
  const [failed, setFailed] = useState([]);
  const fileRef = useRef(null);

  const totalCount = entityIds.length;

  const reset = useCallback(() => {
    setFile(null);
    setStatus('idle');
    setLinkedCount(0);
    setFailed([]);
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const handleClose = () => {
    if (status === 'uploading' || status === 'linking') return; // block close mid-upload
    reset();
    onOpenChange(false);
  };

  const handlePick = (f) => {
    if (!f) return;
    if (f.size > MAX_SIZE_BYTES) {
      toast.error(`${f.name} is over 10 MB.`);
      return;
    }
    setFile(f);
  };

  const handleAttach = async () => {
    if (!file || totalCount === 0) return;
    setStatus('uploading');
    setLinkedCount(0);
    setFailed([]);
    const [firstId, ...rest] = entityIds;

    // 1) Upload ONCE, linked to the first entity.
    let docId = null;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', LEGACY_DOC_TYPE[entityType] || 'invoice_support');
      fd.append('reference_id', firstId);
      fd.append('entity_type', entityType);
      fd.append('entity_id', firstId);
      fd.append('uploaded_via', uploadedVia || entityType);
      const r = await axios.post(`${API}/documents/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      docId = r.data?.document_id;
      if (!docId) throw new Error('missing document_id');
      setLinkedCount(1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
      setStatus('idle');
      return;
    }

    // 2) Link the same doc to every remaining entity.
    if (rest.length > 0) {
      setStatus('linking');
      const failures = [];
      // Sequential to give per-row progress; batch could be parallel but
      // the total count is usually < 20 so latency isn't a real issue.
      let done = 1;
      for (const eid of rest) {
        try {
          await axios.post(`${API}/documents/${docId}/links`, {
            entity_type: entityType,
            entity_id: eid,
            linked_via: 'bulk_attach',
          });
          done += 1;
          setLinkedCount(done);
        } catch (err) {
          failures.push(eid);
        }
      }
      setFailed(failures);
    }
    setStatus('done');
    toast.success(`Attached to ${totalCount - (status === 'done' ? failed.length : 0)} row${totalCount === 1 ? '' : 's'}`);
    if (onCompleted) {
      try { onCompleted(docId); } catch { /* non-fatal */ }
    }
  };

  const progressPct = totalCount ? Math.round((linkedCount / totalCount) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="bulk-attach-docs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Upload className="w-4 h-4 text-[#108981]" />
            Attach document to {totalCount} transaction{totalCount === 1 ? '' : 's'}
          </DialogTitle>
        </DialogHeader>

        {status === 'idle' && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500 leading-relaxed">
              Upload once, and we&apos;ll link the same receipt / contract to every selected row.
              Great for a single bank sweep that pays several invoices.
            </p>
            <label
              className={`block rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${file ? 'border-[#108981] bg-[#108981]/5' : 'border-slate-300 hover:border-[#108981]/60 hover:bg-slate-50'}`}
              data-testid="bulk-attach-drop-zone"
            >
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT_TYPES}
                className="hidden"
                onChange={(e) => handlePick(e.target.files?.[0])}
                data-testid="bulk-attach-file-input"
              />
              {file ? (
                <>
                  <CheckCircle2 className="w-6 h-6 text-[#108981] mx-auto mb-1.5" />
                  <p className="text-sm font-medium text-slate-800 truncate">{file.name}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">Click to change file</p>
                </>
              ) : (
                <>
                  <Upload className="w-6 h-6 text-slate-400 mx-auto mb-1.5" />
                  <p className="text-sm font-medium text-slate-700">Drop a file or click to browse</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">Images, PDFs, spreadsheets · max 10 MB</p>
                </>
              )}
            </label>
          </div>
        )}

        {(status === 'uploading' || status === 'linking') && (
          <div className="py-6 text-center" data-testid="bulk-attach-progress">
            <Loader2 className="w-6 h-6 animate-spin text-[#108981] mx-auto mb-2" />
            <p className="text-sm font-medium">
              {status === 'uploading' ? 'Uploading receipt…' : `Linking ${linkedCount} of ${totalCount}…`}
            </p>
            <div className="mt-3 h-1.5 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full bg-[#108981] transition-all duration-200" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}

        {status === 'done' && (
          <div className="py-4 text-center" data-testid="bulk-attach-done">
            <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
            <p className="text-sm font-semibold text-slate-800">
              Linked to {totalCount - failed.length} of {totalCount} rows
            </p>
            {failed.length > 0 && (
              <p className="mt-1 text-xs text-amber-700 flex items-center justify-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {failed.length} row{failed.length === 1 ? '' : 's'} failed to link
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={handleClose}
            disabled={status === 'uploading' || status === 'linking'}
            data-testid="bulk-attach-close"
          >
            {status === 'done' ? 'Close' : 'Cancel'}
          </Button>
          {status === 'idle' && (
            <Button
              onClick={handleAttach}
              disabled={!file}
              className="bg-[#108981] hover:bg-[#0d766a] gap-1"
              data-testid="bulk-attach-submit"
            >
              <Upload className="w-4 h-4" /> Attach to {totalCount}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkAttachDocsDialog;
